<template>
  <div v-if="this.allSections.length">
    <BaseButton
      text="Back to 'Your quotes'"
      flat
      ripple
      class="font-weight-semi-bold d-flex back-btn back-previous__btn-link pa-0 mb-sm-5"
      icon="mdi-chevron-left"
      color="psdarkgreen"
      small
      @click="backToYourQuotes"
    />
    <div>
      <div class="d-flex justify-space-between align-center mt-6">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          {{ $vuetify.breakpoint.lgAndUp ? "Your application" : "Application" }}
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="psdarkgreen"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <ValidationObserver ref="observerFormApplication" v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <h2 class="mt-10 mb-4 font-20 font-weight-bold primary--text">
          Policy details
        </h2>

        <BaseLabel text="Basis of advice" class="mt-n2 mb-2" />
        <BaseSelect
          v-model="questionBasisOfAdvice.Value"
          dataUiid="BasisOfAdvice"
          :items="basisOfAdviceOptions"
          :rules="{
            required: {
              fieldLabel: 'Basis of advice',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Basis of advice"
        />

        <BaseLabel text="Policy start date (optional)" class="mt-10 mb-2" />
        <BaseInputDate
          :vid="questionPolicyStartDate.InterfaceKey"
          v-model="questionPolicyStartDate.Value"
          dataUiid="PolicyStartDate"
          :rules="questionPolicyStartDateRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Policy start date"
        />

        <BaseLabel text="Document delivery preferences" class="mt-2 mb-2" />
        <BaseRadio
          v-model="questionDocumentDeliveryPreference.Value"
          pslStyle="button"
          class="pt-3"
          data-uiid="DocumentDeliveryPreferencesRadio"
          :hideDetails="false"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :items="documentDeliveryPreferenceOptions"
          aria-label="Document delivery preferences"
        />

        <h2 class="mt-5 mb-4 font-20 font-weight-bold primary--text">
          Applicant details
        </h2>

        <BaseLabel text="Telephone number" class="mt-n2 mb-2" />
        <BaseInput
          :vid="questionApplicant1TelNo.InterfaceKey"
          v-model="questionApplicant1TelNo.Value"
          dataUiid="Applicant1TelNo"
          hideDetails="auto"
          :rules="questionTelephoneNumberRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Telephone number"
        />

        <BaseLabel text="Email address" class="mt-10 mb-2" />
        <BaseInput
          :vid="questionApplicant1EmailAddress.InterfaceKey"
          v-model="questionApplicant1EmailAddress.Value"
          dataUiid="Applicant1EmailAddress"
          hideDetails="auto"
          :rules="questionEmailAddressRules"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Email address"
        />

        <BaseLabel
          text="Do you want to specify a correspondence address?"
          class="mt-10 mb-2"
          helpText="Please indicate if the customer wishes to use an alternative address to receive correspondence."
        />
        <BaseToggleButton
          v-model="questionUseCorrespondenceAddress.Value"
          :toggleButtonText="{
            yesDataUiid: `${questionUseCorrespondenceAddress.InterfaceKey}_Yes`,
            noDataUiid: `${questionUseCorrespondenceAddress.InterfaceKey}_No`,
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '145' : ''"
          class="mb-10"
          aria-label="Do you want to specify a correspondence address?"
        />

        <div
          v-if="questionUseCorrespondenceAddress.Value"
          :class="!enteringCorrespondenceAddressManually ? 'mb-5' : ''"
        >
          <BaseLabel
            v-if="!enteringCorrespondenceAddressManually"
            text="Find your property"
            class="mb-2"
          />

          <BaseAddressLookup
            dataUiid="CorrespondenceAddress"
            manualAddressFormText="Correspondence address"
            :placeholder="
              $vuetify.breakpoint.lgAndUp
                ? 'Start typing your address or postcode...'
                : 'Start typing...'
            "
            manualFormUiid="CorrespondenceAddress"
            id="correspondence-address-lookup"
            @address-selected="
              (response) =>
                addressSelected(response, questionCorrespondenceAddress)
            "
            @enter-address-manually="
              enteringCorrespondenceAddressManually = true
            "
            @use-address-finder="enteringCorrespondenceAddressManually = false"
            :address="questionCorrespondenceAddress"
            :rules="{
              required: {
                fieldLabel: 'Find your property',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            showManualAddressForm
            :hideDetails="false"
            addressSummaryWrapper="mt-10 address-summary__wrapper mb-10"
          />
        </div>

        <div v-if="isSecondApplicantIncluded" class="mb-10">
          <h2 class="mb-4 font-20 font-weight-bold primary--text">
            Second applicant details
          </h2>

          <BaseLabel text="Telephone number" class="mt-n2 mb-2" />
          <BaseInput
            :vid="questionApplicant2TelNo.InterfaceKey"
            v-model="questionApplicant2TelNo.Value"
            dataUiid="Applicant2TelNo"
            hideDetails="auto"
            :rules="questionTelephoneNumberRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Second applicant telephone number"
          />

          <BaseLabel text="Email address" class="mt-10 mb-2" />
          <BaseInput
            :vid="questionApplicant2EmailAddress.InterfaceKey"
            v-model="questionApplicant2EmailAddress.Value"
            dataUiid="Applicant2EmailAddress"
            hideDetails="auto"
            :rules="questionEmailAddressRules"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Second applicant email address"
          />
        </div>

        <h2 class="mb-4 font-20 font-weight-bold primary--text">
          Mortgage details (optional)
        </h2>

        <BaseLabel
          text="Mortgage lender"
          class="mt-n2 mb-2"
          helpText="Where there is a mortgage on the property please provide the name of the mortgage lender. Where no mortgage exists on the property please insert 'N/A'."
        />
        <BaseInput
          :vid="questionMortgageLender.InterfaceKey"
          v-model="questionMortgageLender.Value"
          dataUiid="MortgageLender"
          hideDetails="auto"
          :rules="{
            max: {
              length: 30,
              errorMessageTemplate:
                'Mortgage lender may not be greater than 30 characters.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Mortgage lender"
        />

        <BaseLabel text="Mortgage completion date" class="mt-10 mb-2" />
        <BaseInputDate
          :vid="questionMortgageStartDate.InterfaceKey"
          v-model="questionMortgageStartDate.Value"
          dataUiid="MortgageStartDate"
          :rules="{
            date_valid: {
              fieldLabel: 'Mortgage completion date',
              errorMessageTemplate: 'Mortgage completion date is not valid.',
            },
            regex: {
              pattern: DATE_REGEX,
              customMessage: 'Mortgage completion date is not valid.',
            },
            date_in_range: {
              min: 35,
              max: 1,
              units: 'years',
              customMessage:
                'Mortgage completion date must be less than 35 years in the past and be no more than 1 year into the future.',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
        />

        <h2 class="mt-5 mb-4 font-20 font-weight-bold primary--text">
          Solicitor details (optional)
        </h2>

        <BaseLabel text="Solicitor name" class="mt-n2 mb-2" />
        <BaseInput
          :vid="questionSolicitorName.InterfaceKey"
          v-model="questionSolicitorName.Value"
          dataUiid="SolicitorName"
          hideDetails="auto"
          :rules="{
            regex: {
              pattern: '^[a-zA-Z][a-zA-Z-\'\\s]*$',
              customMessage:
                'First character must be a letter. Only letters, spaces, hyphens, and apostrophes are allowed in this field.',
            },
            maxLength: {
              length: 30,
              fieldLabel: 'Solicitor name',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Solicitor name"
        />

        <div class="mt-10">
          <BaseLabel
            v-if="!enteringSolicitorAddressManually"
            text="Solicitor address"
            class="mb-2"
          />

          <BaseAddressLookup
            dataUiid="SolicitorAddress_Find_Property"
            manualFormUiid="SolicitorAddress"
            manualAddressFormText="Solicitor address"
            :placeholder="
              $vuetify.breakpoint.lgAndUp
                ? 'Start typing your address or postcode...'
                : 'Start typing...'
            "
            @address-selected="
              (response) => addressSelected(response, questionSolicitorAddress)
            "
            @enter-address-manually="enteringSolicitorAddressManually = true"
            @use-address-finder="enteringSolicitorAddressManually = false"
            id="address-lookup-solicitor"
            :address="questionSolicitorAddress"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            showManualAddressForm
            :hideDetails="false"
            addressSummaryWrapper="mt-10 address-summary__wrapper mb-10"
          />
        </div>

        <BaseLabel
          text="Email intention to solicitor?"
          class="mt-5 mb-2"
          helpText="Please indicate if you would like to email a copy of the 'Intention' to accept risk to the customer's solicitor."
        />
        <BaseToggleButton
          v-model="questionEmailAcceptToSolicitor.Value"
          :toggleButtonText="{
            yesDataUiid: `${questionEmailAcceptToSolicitor.InterfaceKey}_Yes`,
            noDataUiid: `${questionEmailAcceptToSolicitor.InterfaceKey}_No`,
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <BaseLabel
          text="Email risk notification to solicitor?"
          class="mt-10 mb-2"
          helpText="Please indicate if you would like to email a copy of the 'Risk Notification' to the customer's solicitor."
        />
        <BaseToggleButton
          v-model="questionEmailNotificationToSolicitor.Value"
          :toggleButtonText="{
            yesDataUiid: `${questionEmailNotificationToSolicitor.InterfaceKey}_Yes`,
            noDataUiid: `${questionEmailNotificationToSolicitor.InterfaceKey}_No`,
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <!-- solicitor email address -->
        <template
          v-if="
            questionEmailAcceptToSolicitor.Value ||
            questionEmailNotificationToSolicitor.Value
          "
        >
          <BaseLabel text="Solicitor email address" class="mt-10 mb-2" />
          <BaseInput
            :vid="questionSolicitorEmailAddress.InterfaceKey"
            v-model="questionSolicitorEmailAddress.Value"
            dataUiid="SolicitorEmailAddress"
            hideDetails="auto"
            :rules="{
              required: {
                fieldLabel: 'Solicitor email address',
              },
              regex: {
                pattern: EMAIL_REGEX,
                customMessage: 'Please enter a valid email address.',
              },
              max: {
                length: 50,
                errorMessageTemplate:
                  'Solicitor email address may not be greater than 50 characters.',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Solicitor email address"
          />
        </template>

        <BaseLabel
          text="Your reference (optional)"
          class="mt-10 mb-2"
          helpText="Should you wish to assign your own reference to this case please enter it here."
        />
        <BaseInput
          :vid="questionYourRef.InterfaceKey"
          v-model="questionYourRef.Value"
          dataUiid="YourRef"
          hideDetails="auto"
          :rules="{
            maxLength: {
              length: 40,
              fieldLabel: 'Your reference (optional)',
            },
            regex: {
              pattern: USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
              customMessage: ERROR_MSG_USER_REF,
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Your reference (optional)"
        />

        <div class="my-10">
          <BaseLabel text="Additional information (optional)" class="mb-2" />
          <BaseInput
            :vid="questionAdditionalInfo.InterfaceKey"
            v-model="questionAdditionalInfo.Value"
            dataUiid="AdditionalInfo"
            hideDetails="auto"
            :rules="{
              maxLength: {
                length: 200,
                fieldLabel: 'Additional information (optional)',
              },
              regex: {
                pattern: '^[a-zA-Z0-9\\s\-\']*$',
                customMessage:
                  'Please enter only alphanumeric characters, spaces, or any of the following characters in this field - \'',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Additional information (optional)"
          />
        </div>

        <template v-if="isAdvisedSeller">
          <BaseAlert
            color="var(--blue-100)"
            class="pa-4 border-sm border-solid border-info"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--blue-500)">mdi-information</v-icon>
              </div>
              <div class="primary--text">
                <span class="font-weight-bold">Important!</span> Before you
                provide payment details, please confirm that you are happy to
                proceed. You have only received limited information about
                Paymentshield, our services and policies. Full details,
                including terms and conditions, benefits, limitations and
                exclusions will be sent to you via your chosen preference in the
                next couple of days.
              </div>
            </div>
          </BaseAlert>

          <BaseCheckBox
            className="mt-3 font-weight-bold primary--text mb-3"
            label="I confirm that I have read the above statement and I am happy to proceed."
            :hideDetails="false"
            :checked="questionLimitedInformationStatement.Value"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Confirmation',
              },
            }"
            @change="(val) => (questionLimitedInformationStatement.Value = val)"
            :vid="questionLimitedInformationStatement.InterfaceKey"
            :dataUiid="questionLimitedInformationStatement.InterfaceKey"
            aria-label="I confirm that I have read the above statement and I am happy to proceed."
          />
        </template>

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 mb-3"
          type="submit"
          color="psorange"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
          :text="
            $vuetify.breakpoint.lgAndUp ? 'CONTINUE TO PAYMENT' : 'CONTINUE'
          "
        />
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import {
  addFieldLabel,
  convertToBool,
  scrollTop,
  tryGoToNextStep,
} from "@/common/utils";
import {
  BASIS_OF_ADVICE,
  BASIS_OF_ADVICE_FOR_ADVISED,
  DOCUMENT_DELIVERY_PREFERENCE_OPTIONS,
  setItemDataUiids,
} from "@/constants/component.constants.js";
import {
  ERROR_MSG_USER_REF,
  ERROR_INVALID,
} from "@/constants/message.constants";
import {
  DATE_REGEX,
  EMAIL_REGEX,
  USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
} from "@/constants/regex-patterns.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters, mapState } from "vuex";

import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import BaseRadio from "@/components/BaseComponents/BaseRadio.vue";
import { EventBus } from "@/common/eventbus";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox.vue";
import BaseAlert from "@/components/BaseComponents/BaseAlert.vue";
import { addressSelected } from "@/common/addresses";
export default {
  name: "B2BHomeYourApplicationPage",
  components: {
    ValidationObserver,
    BaseButton,
    BaseLabel,
    BaseSelect,
    BaseInputDate,
    BaseToggleButton,
    BaseRadio,
    BaseInput,
    BaseAddressLookup,
    BaseAlert,
    BaseCheckBox,
  },
  data() {
    return {
      setItemDataUiids,
      BASIS_OF_ADVICE,
      APPLICATION_MODULE,
      USER_REF_EXCLUDE_ANGLE_BRACKETS_REGEX,
      ERROR_MSG_USER_REF,
      DATE_REGEX,
      EMAIL_REGEX,

      questionBasisOfAdvice: {},
      questionPolicyStartDate: {},
      questionDocumentDeliveryPreference: {},
      questionApplicant1TelNo: {},
      questionApplicant1EmailAddress: {},
      questionUseCorrespondenceAddress: {},
      questionCorrespondenceAddress: {},
      questionApplicant2TelNo: {},
      questionApplicant2EmailAddress: {},
      questionMortgageLender: {},
      questionMortgageStartDate: {},
      questionSolicitorName: {},
      questionSolicitorAddress: {},
      questionEmailAcceptToSolicitor: {},
      questionEmailNotificationToSolicitor: {},
      questionSolicitorEmailAddress: {},
      questionYourRef: {},
      questionAdditionalInfo: {},

      questionLimitedInformationStatement: {},

      enteringCorrespondenceAddressManually: false,
      enteringSolicitorAddressManually: false,

      questionTelephoneNumberRules: {
        required: {
          fieldLabel: "Telephone number",
        },
        phone_number: {
          fieldLabel: "Telephone number",
        },
      },

      questionEmailAddressRules: {
        required: {
          fieldLabel: "Email address",
        },
        regex: {
          pattern: "^[\\w\\-\\.\\+]+\\@[a-zA-Z0-9\\.\\-]+\\.[a-zA-z0-9]{2,}$",
          customMessage: "Please enter a valid email address.",
        },
        max: {
          length: 50,
          errorMessageTemplate:
            "Email address may not be greater than 50 characters.",
        },
      },
    };
  },
  watch: {
    "questionYourRef.Value"(value) {
      this.setUserReference(value);
    },
    "questionUseCorrespondenceAddress.Value"(value) {
      if (!value) {
        this.resetCorrespondenceAddress();
        this.enteringCorrespondenceAddressManually = false;
      }
    },
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["restoringLastStep", "userReference"]),
    ...mapState(APPLICATION_MODULE, ["questionMap"]),
    ...mapGetters(QUESTIONS_MODULE, ["getBoolAnswer", "isAdvisedSeller"]),
    ...mapGetters(APPLICATION_MODULE, ["allSections", "selectedQuote"]),
    isSecondApplicantIncluded() {
      return this.getBoolAnswer("SecondApplicantIncluded");
    },
    basisOfAdviceOptions() {
      return this.isAdvisedSeller
        ? setItemDataUiids("BasisOfAdvice", BASIS_OF_ADVICE_FOR_ADVISED)
        : setItemDataUiids("BasisOfAdvice", BASIS_OF_ADVICE);
    },
    documentDeliveryPreferenceOptions() {
      return setItemDataUiids(
        this.questionDocumentDeliveryPreference.InterfaceKey,
        DOCUMENT_DELIVERY_PREFERENCE_OPTIONS
      );
    },
    questionPolicyStartDateRules() {
      return addFieldLabel(
        {
          date_valid: {
            errorMessageTemplate: ERROR_INVALID,
          },
          regex: {
            errorMessageTemplate: ERROR_INVALID,
            pattern: DATE_REGEX,
          },
          between_now_and: {
            future: this.selectedQuote?.QuoteExpiryDate,
          },
        },
        "Policy start date"
      );
    },
  },
  beforeMount() {
    scrollTop();

    if (this.allSections.length) {
      this.questionBasisOfAdvice = this.questionMap.BasisOfAdvice;
      this.questionPolicyStartDate = this.questionMap.PolicyStartDate;
      this.questionDocumentDeliveryPreference =
        this.questionMap.DocumentDeliveryPreference;

      this.questionApplicant1TelNo = this.questionMap.Applicant1TelNo;
      this.questionApplicant1EmailAddress =
        this.questionMap.Applicant1EmailAddress;

      this.questionUseCorrespondenceAddress =
        this.questionMap.UseCorrespondenceAddress;

      this.questionCorrespondenceAddress = {
        County: this.questionMap.CorrespondenceCounty,
        District: this.questionMap.CorrespondenceDistrict,
        PostCode: this.questionMap.CorrespondencePostCode,
        Street: this.questionMap.CorrespondenceStreet,
        Town: this.questionMap.CorrespondenceTown,
      };

      this.questionApplicant2TelNo = this.questionMap.Applicant2TelNo;
      this.questionApplicant2EmailAddress =
        this.questionMap.Applicant2EmailAddress;
      this.questionMortgageLender = this.questionMap.MortgageLender;
      this.questionMortgageStartDate = this.questionMap.MortgageStartDate;
      this.questionSolicitorName = this.questionMap.SolicitorName;

      this.questionSolicitorAddress.Street =
        this.questionMap.SolicitorAddressStreet;
      this.questionSolicitorAddress.District =
        this.questionMap.SolicitorAddressDistrict;
      this.questionSolicitorAddress.Town =
        this.questionMap.SolicitorAddressTown;
      this.questionSolicitorAddress.County =
        this.questionMap.SolicitorAddressCounty;
      this.questionSolicitorAddress.PostCode =
        this.questionMap.SolicitorAddressPostCode;

      this.questionEmailAcceptToSolicitor =
        this.questionMap.EmailAcceptToSolicitor;
      this.questionEmailNotificationToSolicitor =
        this.questionMap.EmailNotificationToSolicitor;
      this.questionYourRef = this.questionMap.YourRef;
      if (this.userReference) {
        this.questionYourRef.Value = this.userReference;
      }
      this.questionAdditionalInfo = this.questionMap.AdditionalInfo;
      this.questionSolicitorEmailAddress =
        this.questionMap.SolicitorEmailAddress;

      if (this.isAdvisedSeller) {
        this.questionLimitedInformationStatement =
          this.questionMap.LimitedInformationStatement;

        this.questionLimitedInformationStatement.Value = convertToBool(
          this.questionLimitedInformationStatement.Value
        );
      }

      // when retrieving a quote convert "Boolean Strings" to Booleans
      this.questionUseCorrespondenceAddress.Value = convertToBool(
        this.questionUseCorrespondenceAddress.Value
      );

      this.questionEmailAcceptToSolicitor.Value = convertToBool(
        this.questionEmailAcceptToSolicitor.Value
      );

      this.questionEmailNotificationToSolicitor.Value = convertToBool(
        this.questionEmailNotificationToSolicitor.Value
      );
    }

    // clear second applicant contact details
    this.resetSecondApplicantContactDetails();
  },
  mounted() {
    this.setCurrentRefObserverForm(this.$refs.observerFormApplication);
    if (!this.restoringLastStep) {
      return;
    }

    setTimeout(async () => {
      tryGoToNextStep(
        this.$refs.observerFormApplication,
        async (form) => await form.validate(),
        ROUTE_NAME.HOME.B2B.PAYMENT
      );
    }, 0);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "toggleGlobalSaveAndExitDialog",
      "setCurrentRefObserverForm",
      "refreshSessionAndNextStep",
      "setUserReference",
    ]),
    addressSelected,
    backToYourQuotes() {
      this.$router.push({
        name: ROUTE_NAME.HOME.B2B.QUOTATION_RESULT,
      });
    },

    resetCorrespondenceAddress() {
      this.questionMap.CorrespondencePostCode.Value = "";
      this.questionMap.CorrespondenceStreet.Value = "";
      this.questionMap.CorrespondenceDistrict.Value = "";
      this.questionMap.CorrespondenceTown.Value = "";
      this.questionMap.CorrespondenceCounty.Value = "";
    },

    async onSubmit(validate) {
      const isFormValid = await validate();

      if (!isFormValid) {
        EventBus.$emit("validation-error", "Validation error occurred");

        return;
      }

      this.refreshSessionAndNextStep(ROUTE_NAME.HOME.B2B.PAYMENT);
    },

    resetSecondApplicantContactDetails() {
      if (!this.isSecondApplicantIncluded) {
        this.questionApplicant2TelNo.Value = "";
        this.questionApplicant2EmailAddress.Value = "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.find-property) {
  .v-input__append-inner {
    margin: auto 0 !important;
  }
}
</style>
